import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { Package, Navigation, Clock, CheckCircle, XCircle, Send, AlertCircle, Trash2 } from 'lucide-react';
import {
  RENOUNCE_WINDOW_DAYS,
  RENOUNCE_COOLDOWN_DAYS,
  MAX_RENOUNCE_COUNT,
  computeBlockedFor,
  computeCooldownFor,
  computeRenounceEligibility,
} from '@/utils/roleRequests';
import { readRoleRequestParams } from '@/utils/roles';
import '@/styles/roles.css';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ROLE_INFO = {
  ROLE_CARRIER: {
    label: 'Transporteur',
    icon: <Navigation size={22} />,
    color: '#C8832A',
    bg: '#fef9f0',
    border: '#f5d9a5',
    description: 'Proposez vos trajets et transportez des colis pour des expéditeurs. Gagnez de l\'argent sur vos déplacements habituels.',
    requirements: ['Avoir effectué un trajet récemment', 'Identité vérifiée (KYC recommandé)', 'Moyen de transport fiable'],
  },
  ROLE_SENDER: {
    label: 'Expéditeur',
    icon: <Package size={22} />,
    color: '#0e7a68',
    bg: '#ecfdf5',
    border: '#a7f3d0',
    description: 'Publiez vos annonces de colis et trouvez un transporteur disponible sur votre route.',
    requirements: ['Compte vérifié', 'Adresse valide', 'Moyen de paiement enregistré'],
  },
};

const STATUS_CONFIG = {
  PENDING:    { label: 'En attente',  icon: <Clock size={14} />,        color: '#92400e', bg: '#fef3c7' },
  APPROVED:   { label: 'Approuvé',   icon: <CheckCircle size={14} />,  color: '#065f46', bg: '#d1fae5' },
  REJECTED:   { label: 'Refusé',     icon: <XCircle size={14} />,      color: '#991b1b', bg: '#fee2e2' },
  RENOUNCED:  { label: 'Renoncé',    icon: <XCircle size={14} />,      color: '#6d28d9', bg: '#ede9fe' },
  SUPERSEDED: { label: 'Remplacé',   icon: <XCircle size={14} />,      color: '#9CA3AF', bg: '#F3F4F6' },
};

// Constantes et logique pure importées depuis @/utils/roleRequests (testables unitairement)

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px',
      borderRadius: 20, fontSize: 12, fontWeight: 600,
      color: cfg.color, background: cfg.bg,
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function RequestCard({ role, userRoles, pending, cooldown, onRequest }) {
  const info = ROLE_INFO[role];
  const hasRole    = userRoles.includes(role);
  const isApproved = pending?.status === 'APPROVED';
  const isPending  = pending?.status === 'PENDING';

  return (
    <div style={{
      border: `1.5px solid ${hasRole ? info.border : '#e5e7eb'}`,
      borderRadius: 14, padding: '20px 22px',
      background: hasRole ? info.bg : '#fff',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{
          color: info.color, background: info.bg,
          borderRadius: 10, padding: 10, lineHeight: 1,
          border: `1px solid ${info.border}`,
        }}>
          {info.icon}
        </span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>
            Rôle {info.label}
          </div>
          <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
            {info.description}
          </div>
        </div>
      </div>

      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
        {info.requirements.map((r, i) => <li key={i}>{r}</li>)}
      </ul>

      {hasRole ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#065f46', fontSize: 13, fontWeight: 600 }}>
          <CheckCircle size={15} /> Vous avez déjà ce rôle
        </div>
      ) : isApproved ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#065f46', fontSize: 13, fontWeight: 600 }}>
          <CheckCircle size={15} /> Rôle approuvé. Reconnectez-vous pour l'activer
        </div>
      ) : isPending ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#92400e', fontSize: 13, fontWeight: 600 }}>
          <Clock size={15} /> Demande en cours, en attente de validation admin
        </div>
      ) : cooldown?.blocked ? (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '10px 12px', borderRadius: 8,
          background: '#fef2f2', border: '1px solid #fca5a5',
          fontSize: 13, color: '#991b1b',
        }}>
          <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>Accès bloqué.</strong> Vous avez renoncé {cooldown.renounceCount} fois à ce rôle.
            Contactez le support pour le récupérer.
          </span>
        </div>
      ) : cooldown?.inCooldown ? (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '10px 12px', borderRadius: 8,
          background: '#faf5ff', border: '1px solid #d8b4fe',
          fontSize: 13, color: '#6d28d9',
        }}>
          <Clock size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>Cooldown.</strong> Re-demande possible dans <strong>{cooldown.daysLeft} jour{cooldown.daysLeft > 1 ? 's' : ''}</strong>.
            {cooldown.renounceCount > 1 && ` (${MAX_RENOUNCE_COUNT - cooldown.renounceCount} renonciation${MAX_RENOUNCE_COUNT - cooldown.renounceCount > 1 ? 's' : ''} restante${MAX_RENOUNCE_COUNT - cooldown.renounceCount > 1 ? 's' : ''} avant blocage définitif)`}
          </span>
        </div>
      ) : (
        <button
          onClick={() => onRequest(role)}
          style={{
            background: info.color, color: '#fff', border: 'none',
            borderRadius: 8, padding: '10px 18px', fontSize: 13.5,
            fontWeight: 600, cursor: 'pointer', display: 'flex',
            alignItems: 'center', gap: 8, alignSelf: 'flex-start',
          }}
        >
          <Send size={14} /> Demander le rôle {info.label}
        </button>
      )}
    </div>
  );
}

export default function RoleRequestPage() {
  const { user, checkAuth } = useAuth();
  const toast = useToast();
  const location = useLocation();
  // Arrivée depuis « Publier un trajet », « Vous voyagez ? », « Accès refusé »… : rôle demandé et page à rouvrir ensuite
  const arrival = useMemo(() => readRoleRequestParams(location.search), [location.search]);
  const arrivalOpened = useRef(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null); // 'ROLE_CARRIER' | 'ROLE_SENDER' | null
  const [reason, setReason] = useState('');
  const [profileType, setProfileType] = useState('PERSONAL'); // 'PERSONAL' | 'PROFESSIONAL'
  const [companyName, setCompanyName] = useState('');
  const [siret, setSiret] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [renounceTarget, setRenounceTarget] = useState(null); // role string à renoncer
  const [renouncing, setRenouncing] = useState(false);

  const userRoles = useMemo(() => user?.roles || [], [user?.roles]);
  // Un compte Entreprise le reste : une demande de rôle ne le fait jamais repasser en Particulier
  const alreadyCompany = user?.profile_type === 'PROFESSIONAL';
  const newCompany = profileType === 'PROFESSIONAL' && !alreadyCompany;

  const renounceEligibility = useMemo(
    () => computeRenounceEligibility(requests, userRoles),
    [requests, userRoles],
  );

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }] = await Promise.all([axios.get(`${API}/users/me/role-requests`), checkAuth()]);
      setRequests(Array.isArray(data) ? data : []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const blockedFor = (role) => computeBlockedFor(requests, role);

  const cooldownFor = useMemo(
    () => computeCooldownFor(requests, Object.keys(ROLE_INFO)),
    [requests],
  );

  // Arrivée sur un rôle à demander : le formulaire s'ouvre seul, une fois, si rien ne s'y oppose
  const arrivalPending = arrival.role ? blockedFor(arrival.role) : null;
  const arrivalHasRole = !!arrival.role && userRoles.includes(arrival.role);
  useEffect(() => {
    if (loading || !arrival.role || arrivalOpened.current) return;
    arrivalOpened.current = true;
    const cd = cooldownFor[arrival.role];
    if (!arrivalHasRole && !arrivalPending && !cd?.blocked && !cd?.inCooldown) handleRequest(arrival.role);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRenounce = async () => {
    if (!renounceTarget) return;
    setRenouncing(true);
    try {
      await axios.delete(`${API}/users/me/roles/${renounceTarget}`);
      toast.success(`Rôle ${ROLE_INFO[renounceTarget]?.label} retiré de votre compte.`);
      setRenounceTarget(null);
      await checkAuth(); // refresh user.roles
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la renonciation.');
    } finally {
      setRenouncing(false);
    }
  };

  const handleRequest = (role) => {
    setShowForm(role);
    setReason('');
    setProfileType(alreadyCompany ? 'PROFESSIONAL' : 'PERSONAL');
    setCompanyName(''); setSiret('');
    setModalError(null);
  };

  const handleSubmit = async () => {
    if (reason.trim().length < 10) {
      setModalError('Décrivez votre motivation (minimum 10 caractères).');
      return;
    }
    if (newCompany && (companyName.trim().length < 2 || siret.trim().length < 5)) {
      setModalError("Indiquez le nom de votre société et son SIRET (ou numéro d'immatriculation).");
      return;
    }
    setModalError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/users/me/role-requests`, {
        requested_role: showForm,
        profile_type: profileType,
        reason: reason.trim(),
        ...(arrival.next && arrival.role === showForm ? { next_path: arrival.next } : {}),
        ...(newCompany ? { company_name: companyName.trim(), siret: siret.trim() } : {}),
      });
      setShowForm(null);
      setReason('');
      load();
      toast.success('Demande envoyée : notre équipe l’examine, une notification vous préviendra.');
    } catch (err) {
      setModalError(err?.response?.data?.detail || 'Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div className="mlc-topbar" style={{ marginBottom: 24 }}>
        <div>
          <div className="c2s-topbar-title">Demandes de rôles</div>
          <div className="c2s-topbar-sub">Étendez vos capacités en demandant un rôle supplémentaire</div>
        </div>
      </div>

      {arrival.role && !loading && arrivalHasRole && arrival.next && (
        <div className="rr-arrival" data-testid="rr-arrival-ready">
          <CheckCircle size={18} aria-hidden="true" />
          <span>Vous avez le rôle {ROLE_INFO[arrival.role].label}. <Link to={arrival.next}>Continuer</Link></span>
        </div>
      )}
      {arrival.role && !loading && !arrivalHasRole && arrivalPending?.status === 'PENDING' && (
        <div className="rr-arrival" data-testid="rr-arrival-pending">
          <Clock size={18} aria-hidden="true" />
          <span>
            Votre demande de rôle {ROLE_INFO[arrival.role].label} est en cours d’examen. Dès qu’elle est validée, une
            notification vous ramène à la page voulue.
          </span>
        </div>
      )}

      {/* Rôles actuels */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 10 }}>
          Vos rôles actuels
        </div>
        {userRoles.length === 0 ? (
          <span style={{ fontSize: 13, color: '#9CA3AF' }}>Aucun rôle assigné</span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {userRoles.filter(r => ROLE_INFO[r]).map(r => {
              const info = ROLE_INFO[r];
              const elig = renounceEligibility[r];
              return (
                <div key={r} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: 10,
                  padding: '12px 16px', borderRadius: 12,
                  background: info.bg, border: `1.5px solid ${info.border}`,
                }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    fontSize: 13.5, fontWeight: 700, color: info.color,
                  }}>
                    {info.icon} {info.label}
                    <CheckCircle size={14} color={info.color} />
                  </span>
                  {elig?.eligible ? (
                    <button
                      type="button"
                      onClick={() => setRenounceTarget(r)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 12, fontWeight: 600, color: '#dc2626',
                        background: '#fff', border: '1.5px solid #fca5a5',
                        borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={12} />
                      Renoncer · {elig.daysLeft}j restant{elig.daysLeft > 1 ? 's' : ''}
                    </button>
                  ) : elig ? (
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                      Délai de renonciation expiré ({elig.daysAgo}j)
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cards de demande */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
        {Object.keys(ROLE_INFO).map(role => (
          <RequestCard
            key={role}
            role={role}
            userRoles={userRoles}
            pending={blockedFor(role)}
            cooldown={cooldownFor[role]}
            onRequest={handleRequest}
          />
        ))}
      </div>

      {/* Formulaire de demande */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 16,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 28,
            width: '100%', maxWidth: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,.2)',
          }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6, color: '#111827' }}>
              Demander le rôle {ROLE_INFO[showForm]?.label}
            </div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>
              Expliquez pourquoi vous souhaitez ce rôle. Votre demande sera examinée sous 24-48h.
            </div>

            {alreadyCompany && (
              <div style={{ fontSize: 13, color: '#1E3A5F', background: '#E8EEF6', borderRadius: 10, padding: '10px 12px', marginBottom: 20 }} data-testid="role-request-company-kept">
                Votre compte Entreprise est conservé pour ce nouveau rôle.
              </div>
            )}

            {/* Sélecteur Pro / Perso */}
            {!alreadyCompany && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                Type de profil *
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  {
                    key: 'PERSONAL',
                    label: 'Particulier',
                    emoji: '👤',
                    desc: 'Usage personnel, entre particuliers',
                  },
                  {
                    key: 'PROFESSIONAL',
                    label: 'Entreprise',
                    emoji: '🏢',
                    desc: "Pour le compte d'une société (SIREN)",
                  },
                ].map(opt => {
                  const active = profileType === opt.key;
                  const color = ROLE_INFO[showForm]?.color || '#0e7a68';
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setProfileType(opt.key)}
                      style={{
                        border: `2px solid ${active ? color : '#E5E7EB'}`,
                        borderRadius: 10, padding: '12px 14px',
                        background: active ? `${color}10` : '#fff',
                        textAlign: 'left', cursor: 'pointer',
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                    >
                      <div style={{ fontSize: 20, marginBottom: 4 }}>{opt.emoji}</div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: active ? color : '#111827', marginBottom: 2 }}>
                        {opt.label}
                      </div>
                      <div style={{ fontSize: 11, color: '#6B7280', lineHeight: 1.3 }}>
                        {opt.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {newCompany && (
              <div style={{ display: 'grid', gap: 8, marginBottom: 20 }} data-testid="role-request-company-fields">
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }} htmlFor="rr-company">Nom de la société *</label>
                <input id="rr-company" className="field-input" value={companyName} onChange={e => { setCompanyName(e.target.value); setModalError(null); }} data-testid="input-rr-company" />
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }} htmlFor="rr-siret">SIRET ou numéro d'immatriculation *</label>
                <input id="rr-siret" className="field-input" value={siret} onChange={e => { setSiret(e.target.value); setModalError(null); }} data-testid="input-rr-siret" />
                <div style={{ fontSize: 11.5, color: '#6B7280' }}>Vérifié par notre équipe : la mention « Entreprise » s'affichera après validation.</div>
              </div>
            )}

            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
              Votre motivation *
            </label>
            <textarea
              value={reason}
              onChange={e => { setReason(e.target.value); setModalError(null); }}
              placeholder={showForm === 'ROLE_CARRIER'
                ? 'Ex: Je voyage régulièrement entre Paris et Dakar pour le travail et souhaite rentabiliser mes déplacements...'
                : 'Ex: J\'ai des proches en Afrique à qui j\'envoie régulièrement des colis et je cherche une solution plus économique...'
              }
              rows={5}
              style={{
                width: '100%', borderRadius: 8,
                border: `1.5px solid ${modalError ? '#fca5a5' : '#D1D5DB'}`,
                padding: '10px 12px', fontSize: 13.5, resize: 'vertical',
                fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, minHeight: 18 }}>
              <span style={{ fontSize: 11, color: reason.trim().length >= 10 ? '#059669' : '#9CA3AF' }}>
                {reason.trim().length} / min. 10 caractères
              </span>
            </div>
            {modalError && (
              <div style={{
                marginTop: 10, padding: '8px 12px', borderRadius: 8,
                background: '#fee2e2', border: '1px solid #fca5a5',
                fontSize: 13, color: '#991b1b',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                {modalError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => { setShowForm(null); setModalError(null); }}
                style={{
                  flex: 1, border: '1.5px solid #E5E7EB', background: '#fff',
                  borderRadius: 8, padding: '10px', fontSize: 13.5, fontWeight: 600,
                  cursor: 'pointer', color: '#374151',
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  flex: 2, background: ROLE_INFO[showForm]?.color || '#0e7a68',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px', fontSize: 13.5, fontWeight: 600,
                  cursor: submitting ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <Send size={15} />
                {submitting ? 'Envoi en cours...' : 'Envoyer la demande'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Historique des demandes */}
      {!loading && requests.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>
            Historique de vos demandes
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {requests.map(req => (
              <div key={req.id} style={{
                border: '1px solid #E5E7EB', borderRadius: 10,
                padding: '14px 16px', background: '#fafafa',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 13.5, color: '#111827' }}>
                      {ROLE_INFO[req.requested_role]?.label || req.requested_role}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                      background: req.profile_type === 'PROFESSIONAL' ? '#eff6ff' : '#f3f4f6',
                      color: req.profile_type === 'PROFESSIONAL' ? '#1d4ed8' : '#6B7280',
                    }}>
                      {req.profile_type === 'PROFESSIONAL' ? '🏢 Entreprise' : '👤 Particulier'}
                    </span>
                    {req.is_rerequest && (
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                        background: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5',
                      }}>
                        ↩ Re-demande #{(req.previous_renounce_count || 0) + 1}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: req.reject_reason ? 6 : 0 }}>
                    {new Date(req.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  {req.reject_reason && (
                    <div style={{
                      display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 6,
                      fontSize: 12, color: '#991b1b', background: '#fee2e2',
                      padding: '6px 10px', borderRadius: 6,
                    }}>
                      <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>Motif : {req.reject_reason}</span>
                    </div>
                  )}
                </div>
                <StatusBadge status={req.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modale de confirmation de renonciation */}
      {renounceTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 16,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 28,
            width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,.2)',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: '#fee2e2', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <Trash2 size={22} color="#dc2626" />
            </div>
            <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 17, color: '#111827', marginBottom: 8 }}>
              Renoncer au rôle {ROLE_INFO[renounceTarget]?.label} ?
            </div>
            <div style={{ textAlign: 'center', fontSize: 13, color: '#6B7280', marginBottom: 20, lineHeight: 1.6 }}>
              Cette action est <strong>irréversible</strong>. Vous perdrez l'accès aux fonctionnalités
              liées à ce rôle et devrez refaire une demande pour le récupérer.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => setRenounceTarget(null)}
                disabled={renouncing}
                style={{
                  flex: 1, border: '1.5px solid #E5E7EB', background: '#fff',
                  borderRadius: 10, padding: '11px', fontSize: 13.5, fontWeight: 600,
                  cursor: 'pointer', color: '#374151',
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleRenounce}
                disabled={renouncing}
                style={{
                  flex: 2, background: '#dc2626', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '11px', fontSize: 13.5, fontWeight: 600,
                  cursor: renouncing ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <Trash2 size={14} />
                {renouncing ? 'Suppression...' : 'Confirmer la renonciation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
